import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge, Box, Button, Center, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay,
  Flex, HStack, Image, Input, InputGroup, InputLeftElement, Link, Select, SimpleGrid, Spinner, Table, Tbody, Td, Text,
  Th, Thead, Tooltip, Tr, VStack, Wrap, WrapItem, useBreakpointValue, useToast,
} from "@chakra-ui/react";
import { ExternalLink, Lock, LockOpen, Search, Undo2 } from "lucide-react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip as ChartTooltip, Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ConfirmDialog from "../ConfirmDialog";
import { fixarMlAdsAnuncio, getMlAdsAnuncioDiario, getMlAdsAnuncios } from "../../services/api";
import {
  ACCENT, ACOES_HISTORICO, VERDE, VERMELHO, Vazio, brl, corRoas, dataBR, dataHoraBR, erroMsg, num, pct, roasTxt,
} from "./comum";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

const SITUACOES = [
  { valor: "na_campanha", label: "Na campanha" },
  { valor: "fora", label: "Fora" },
  { valor: "espera", label: "Em espera" },
  { valor: "fixados", label: "Fixados" },
  { valor: "erro", label: "Com erro" },
  { valor: "todos", label: "Todos" },
];

const ORDENS = [
  { valor: "custo", label: "Maior investimento" },
  { valor: "receita", label: "Maior receita" },
  { valor: "roas", label: "Melhor ROAS" },
  { valor: "cliques", label: "Mais cliques" },
  { valor: "impressoes", label: "Mais impressões" },
  { valor: "ctr", label: "Maior CTR" },
  { valor: "vendas", label: "Mais vendas" },
  { valor: "entrou", label: "Entrada mais recente" },
];

function situacaoDoItem(i) {
  if (i.fixado === "fora") return { label: "Fixado fora", cor: "purple" };
  if (i.campaign_id) return { label: i.fixado === "dentro" ? "Fixado dentro" : "Na campanha", cor: i.fixado ? "blue" : "green" };
  const hoje = new Date().toISOString().slice(0, 10);
  if (i.bloqueado_ate && i.bloqueado_ate >= hoje) return { label: `Espera até ${dataBR(i.bloqueado_ate)}`, cor: "orange" };
  return { label: "Fora", cor: "gray" };
}

function Miniatura({ src }) {
  return (
    <Box w="40px" h="40px" flexShrink={0} borderRadius="md" overflow="hidden" bg="gray.100">
      {src && <Image src={src} alt="" w="40px" h="40px" objectFit="cover" loading="lazy" />}
    </Box>
  );
}

function CabecalhoAnuncio({ i }) {
  return (
    <HStack spacing={2} align="flex-start" minW={0}>
      <Miniatura src={i.imagem} />
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="bold" noOfLines={1}>{i.codigo || `#${i.produto_id || "—"}`}</Text>
        <Text fontSize="xs" color="gray.600" noOfLines={1}>{i.nome || "—"}</Text>
        <HStack spacing={1} fontSize="xs" color="gray.500">
          <Text>Conta {i.conta_id}</Text>
          <Text>·</Text>
          {i.link ? (
            <Link href={i.link} isExternal color="blue.500" onClick={(e) => e.stopPropagation()}>
              {i.id_ml} <ExternalLink size={10} style={{ display: "inline" }} />
            </Link>
          ) : <Text>{i.id_ml}</Text>}
        </HStack>
      </Box>
    </HStack>
  );
}

function Detalhe({ item, periodo, onFechar, onFixar }) {
  const [dados, setDados] = useState(null);

  useEffect(() => {
    let vivo = true;
    setDados(null);
    if (item) {
      getMlAdsAnuncioDiario(item.id, { de: periodo.de, ate: periodo.ate }).then((r) => vivo && setDados(r || { serie: [], acoes: [] }));
    }
    return () => { vivo = false; };
  }, [item, periodo.de, periodo.ate]);

  const grafico = useMemo(() => {
    const serie = dados?.serie || [];
    return {
      labels: serie.map((d) => dataBR(d.data).slice(0, 5)),
      datasets: [
        { label: "Investimento", data: serie.map((d) => d.custo), borderColor: VERMELHO, tension: 0.3, pointRadius: 1 },
        { label: "Receita", data: serie.map((d) => d.receita), borderColor: VERDE, tension: 0.3, pointRadius: 1 },
      ],
    };
  }, [dados]);

  if (!item) return null;
  const m = item.metricas || {};
  const sit = situacaoDoItem(item);

  return (
    <Drawer isOpen={!!item} placement="right" size={{ base: "full", md: "md" }} onClose={onFechar}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pr={10}>
          <CabecalhoAnuncio i={item} />
        </DrawerHeader>
        <DrawerBody pb={8}>
          <Wrap spacing={2} mb={3}>
            <WrapItem><Badge colorScheme={sit.cor}>{sit.label}</Badge></WrapItem>
            {item.regra && <WrapItem><Badge colorScheme="purple" variant="subtle">Regra: {item.regra.nome}</Badge></WrapItem>}
            {item.fornecedor && <WrapItem><Badge variant="subtle">{item.fornecedor}</Badge></WrapItem>}
            {item.ad_group_status && <WrapItem><Badge variant="outline">Ads: {item.ad_group_status}</Badge></WrapItem>}
          </Wrap>

          {item.motivo_saida && !item.campaign_id && (
            <Text fontSize="sm" color="gray.600" mb={2}>Saiu em {dataHoraBR(item.saiu_em)}: {item.motivo_saida}</Text>
          )}
          {item.ultimo_erro && (
            <Text fontSize="sm" color="red.600" mb={2}>Erro do ML em {dataHoraBR(item.erro_em)}: {item.ultimo_erro}</Text>
          )}

          <SimpleGrid columns={3} spacing={3} mb={4}>
            <Box><Text fontSize="2xs" color="gray.500">INVESTIMENTO</Text><Text fontWeight="bold" color={VERMELHO}>{brl(m.custo)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">RECEITA</Text><Text fontWeight="bold" color={VERDE}>{brl(m.receita)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">ROAS</Text><Text fontWeight="bold" color={corRoas(m.roas)}>{roasTxt(m.roas)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">IMPRESSÕES</Text><Text fontWeight="bold">{num(m.impressoes)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">CLIQUES · CTR</Text><Text fontWeight="bold">{num(m.cliques)} · {pct(m.ctr)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">VENDAS</Text><Text fontWeight="bold">{num(m.vendas)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">PREÇO</Text><Text fontWeight="bold">{item.preco !== null ? brl(item.preco) : "—"}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">MARGEM BRUTA</Text><Text fontWeight="bold">{pct(item.margem_pct, 1)}</Text></Box>
            <Box><Text fontSize="2xs" color="gray.500">ESTOQUE TAGA · FORN.</Text><Text fontWeight="bold">{num(item.estoque_taga)} · {num(item.estoque_fornecedor)}</Text></Box>
          </SimpleGrid>

          <Box h="200px" mb={4}>
            {dados ? (
              <Line data={grafico} options={{
                responsive: true, maintainAspectRatio: false, interaction: { mode: "index", intersect: false },
                plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 10 } } } },
                scales: { x: { ticks: { maxTicksLimit: 8, font: { size: 10 } }, grid: { display: false } }, y: { beginAtZero: true, ticks: { font: { size: 10 } } } },
              }} />
            ) : <Center h="100%"><Spinner size="sm" color={ACCENT} /></Center>}
          </Box>

          <Text fontWeight="bold" fontSize="sm" mb={2}>O que fazer com este anúncio</Text>
          <VStack align="stretch" spacing={2} mb={5}>
            {item.fixado !== "fora" && (
              <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<Lock size={14} />} onClick={() => onFixar(item, "fora")}>
                Tirar e não deixar a regra colocar de volta
              </Button>
            )}
            {item.campaign_id > 0 && item.fixado !== "dentro" && (
              <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<LockOpen size={14} />} onClick={() => onFixar(item, "dentro")}>
                Manter sempre na campanha (ignora desempenho)
              </Button>
            )}
            {(item.fixado || item.bloqueado_ate) && (
              <Button size="sm" variant="outline" leftIcon={<Undo2 size={14} />} onClick={() => onFixar(item, null)}>
                Devolver para a regra decidir
              </Button>
            )}
            <Text fontSize="xs" color="gray.500">
              Tirar pelo painel do Mercado Livre não adianta: a regra coloca de volta no dia seguinte. Use os botões acima.
            </Text>
          </VStack>

          <Text fontWeight="bold" fontSize="sm" mb={2}>Histórico</Text>
          {!dados ? <Spinner size="sm" /> : (dados.acoes || []).length === 0 ? (
            <Text fontSize="sm" color="gray.500">Sem movimentação.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {dados.acoes.map((a, idx) => (
                <Box key={idx} fontSize="sm" borderLeft="3px solid" borderColor={a.sucesso ? "gray.200" : "red.300"} pl={2}>
                  <HStack spacing={2}>
                    <Badge colorScheme={ACOES_HISTORICO[a.acao]?.cor || "gray"}>{ACOES_HISTORICO[a.acao]?.label || a.acao}</Badge>
                    <Text fontSize="xs" color="gray.500">{dataHoraBR(a.created_at)}{a.usuario_id ? "" : " · automático"}</Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.700">{a.motivo}</Text>
                  {!a.sucesso && a.erro && <Text fontSize="xs" color="red.600">{a.erro}</Text>}
                </Box>
              ))}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default function AnunciosTab({ conta, periodo, opcoes, regras }) {
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [filtros, setFiltros] = useState({ situacao: "na_campanha", campanha: "", regra: "", busca: "", ordem: "custo" });
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [fixar, setFixar] = useState(null);
  const [fixando, setFixando] = useState(false);
  const ultimaBusca = useRef(0);

  // Busca só dispara depois de uma pausa na digitação.
  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.busca === busca ? f : { ...f, busca })), 400);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => { setPagina(1); }, [filtros, conta, periodo.de, periodo.ate]);

  const carregar = useCallback(async () => {
    const minha = ++ultimaBusca.current;
    setCarregando(true);
    const r = await getMlAdsAnuncios({
      conta: conta || undefined,
      campanha: filtros.campanha || undefined,
      regra: filtros.regra || undefined,
      situacao: filtros.situacao,
      busca: filtros.busca || undefined,
      ordem: filtros.ordem,
      de: periodo.de,
      ate: periodo.ate,
      pagina,
      por_pagina: 30,
    });
    if (minha !== ultimaBusca.current) return; // resposta atrasada de filtro antigo
    setDados(r);
    setCarregando(false);
    if (!r) toast({ title: "Não foi possível carregar os anúncios.", status: "error", duration: 5000 });
  }, [conta, filtros, periodo.de, periodo.ate, pagina, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const campanhas = useMemo(
    () => (opcoes?.contas || []).filter((c) => !conta || String(c.id) === String(conta))
      .flatMap((c) => (c.campanhas || []).map((cp) => ({ ...cp, conta: c.id }))),
    [opcoes, conta]
  );

  const confirmarFixar = async () => {
    if (!fixar) return;
    setFixando(true);
    try {
      await fixarMlAdsAnuncio(fixar.item.id, fixar.valor);
      toast({ title: "Feito.", status: "success", duration: 2500 });
      setFixar(null);
      setAberto(null);
      carregar();
    } catch (e) {
      toast({ title: "Não foi possível", description: erroMsg(e), status: "error", duration: 8000, isClosable: true });
    } finally {
      setFixando(false);
    }
  };

  const contagem = dados?.contagem || {};
  const itens = dados?.itens || [];
  const total = dados?.total || 0;
  const porPagina = dados?.por_pagina || 30;
  const inicio = total ? (pagina - 1) * porPagina + 1 : 0;
  const fim = Math.min(total, pagina * porPagina);

  const textoFixar = fixar?.valor === "fora"
    ? "O anúncio sai da campanha agora e a regra não coloca de volta até você devolver."
    : fixar?.valor === "dentro"
      ? "O anúncio fica na campanha mesmo com desempenho ruim. A regra não tira."
      : "A regra volta a decidir sobre este anúncio na próxima execução (e a espera é zerada).";

  return (
    <Box p={{ base: 3, md: 4 }}>
      <Flex gap={2} mb={3} flexWrap="wrap" overflowX="auto">
        {SITUACOES.map((s) => (
          <Button key={s.valor} size="xs" flexShrink={0} variant={filtros.situacao === s.valor ? "solid" : "outline"}
            colorScheme={filtros.situacao === s.valor ? "orange" : "gray"}
            onClick={() => setFiltros((f) => ({ ...f, situacao: s.valor }))}>
            {s.label} {contagem[s.valor] !== undefined ? `(${num(contagem[s.valor])})` : ""}
          </Button>
        ))}
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={2} mb={3}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none"><Search size={14} color="#A0AEC0" /></InputLeftElement>
          <Input placeholder="código, nome, MLB ou SKU" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </InputGroup>
        <Select size="sm" value={filtros.campanha} onChange={(e) => setFiltros((f) => ({ ...f, campanha: e.target.value }))}>
          <option value="">Todas as campanhas</option>
          {campanhas.map((c) => <option key={`${c.conta}-${c.id}`} value={c.id}>Conta {c.conta} · {c.nome}</option>)}
        </Select>
        <Select size="sm" value={filtros.regra} onChange={(e) => setFiltros((f) => ({ ...f, regra: e.target.value }))}>
          <option value="">Todas as regras</option>
          {(regras || []).map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
        </Select>
        <Select size="sm" value={filtros.ordem} onChange={(e) => setFiltros((f) => ({ ...f, ordem: e.target.value }))}>
          {ORDENS.map((o) => <option key={o.valor} value={o.valor}>{o.label}</option>)}
        </Select>
      </SimpleGrid>

      <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100" position="relative" minH="120px">
        {carregando && (
          <Center position="absolute" inset={0} bg="whiteAlpha.700" zIndex={1} borderRadius="lg"><Spinner color={ACCENT} /></Center>
        )}

        {!carregando && itens.length === 0 && (
          <Vazio titulo="Nenhum anúncio aqui">
            {contagem.todos
              ? "Nenhum anúncio nesta situação com os filtros escolhidos."
              : "Os anúncios aparecem quando uma regra roda: ela procura o grupo de cada anúncio no Ads e acompanha quem entra e sai."}
          </Vazio>
        )}

        {itens.length > 0 && isMobile && (
          <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
            {itens.map((i) => {
              const sit = situacaoDoItem(i);
              const m = i.metricas || {};
              return (
                <Box key={i.id} p={3} onClick={() => setAberto(i)} cursor="pointer" _active={{ bg: "gray.50" }}>
                  <Flex justify="space-between" gap={2} align="flex-start">
                    <CabecalhoAnuncio i={i} />
                    <Badge colorScheme={sit.cor} flexShrink={0} fontSize="0.65em">{sit.label}</Badge>
                  </Flex>
                  <SimpleGrid columns={4} spacing={1} mt={2}>
                    <Box><Text fontSize="2xs" color="gray.500">Invest.</Text><Text fontSize="xs" fontWeight="bold" color={VERMELHO}>{brl(m.custo)}</Text></Box>
                    <Box><Text fontSize="2xs" color="gray.500">Receita</Text><Text fontSize="xs" fontWeight="bold" color={VERDE}>{brl(m.receita)}</Text></Box>
                    <Box><Text fontSize="2xs" color="gray.500">ROAS</Text><Text fontSize="xs" fontWeight="bold" color={corRoas(m.roas)}>{roasTxt(m.roas)}</Text></Box>
                    <Box><Text fontSize="2xs" color="gray.500">Cliques</Text><Text fontSize="xs" fontWeight="bold">{num(m.cliques)}</Text></Box>
                  </SimpleGrid>
                </Box>
              );
            })}
          </VStack>
        )}

        {itens.length > 0 && !isMobile && (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Anúncio</Th>
                  <Th>Situação</Th>
                  <Th isNumeric>Preço</Th>
                  <Th isNumeric>Estoque</Th>
                  <Th isNumeric>Impr.</Th>
                  <Th isNumeric>Cliques</Th>
                  <Th isNumeric>CTR</Th>
                  <Th isNumeric>Invest.</Th>
                  <Th isNumeric>Receita</Th>
                  <Th isNumeric>ROAS</Th>
                  <Th isNumeric>Vendas</Th>
                </Tr>
              </Thead>
              <Tbody>
                {itens.map((i) => {
                  const sit = situacaoDoItem(i);
                  const m = i.metricas || {};
                  return (
                    <Tr key={i.id} _hover={{ bg: "gray.50", cursor: "pointer" }} onClick={() => setAberto(i)}>
                      <Td maxW="320px"><CabecalhoAnuncio i={i} /></Td>
                      <Td>
                        <Badge colorScheme={sit.cor}>{sit.label}</Badge>
                        {i.regra && <Text fontSize="2xs" color="gray.500" mt={1} noOfLines={1}>{i.regra.nome}</Text>}
                        {i.ultimo_erro && (
                          <Tooltip label={i.ultimo_erro} hasArrow><Text fontSize="2xs" color="red.500">erro do ML</Text></Tooltip>
                        )}
                      </Td>
                      <Td isNumeric>{i.preco !== null ? brl(i.preco) : "—"}</Td>
                      <Td isNumeric><Tooltip label="TAGA · fornecedor" hasArrow><span>{num(i.estoque_taga)} · {num(i.estoque_fornecedor)}</span></Tooltip></Td>
                      <Td isNumeric>{num(m.impressoes)}</Td>
                      <Td isNumeric>{num(m.cliques)}</Td>
                      <Td isNumeric>{pct(m.ctr)}</Td>
                      <Td isNumeric color={VERMELHO}>{brl(m.custo)}</Td>
                      <Td isNumeric color={VERDE}>{brl(m.receita)}</Td>
                      <Td isNumeric fontWeight="bold" color={corRoas(m.roas)}>{roasTxt(m.roas)}</Td>
                      <Td isNumeric>{num(m.vendas)}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {total > porPagina && (
        <Flex justify="space-between" align="center" mt={3} gap={2}>
          <Text fontSize="xs" color="gray.500">{num(inicio)}–{num(fim)} de {num(total)}</Text>
          <HStack>
            <Button size="sm" variant="outline" onClick={() => setPagina((p) => Math.max(1, p - 1))} isDisabled={pagina <= 1 || carregando}>Anterior</Button>
            <Button size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)} isDisabled={fim >= total || carregando}>Próxima</Button>
          </HStack>
        </Flex>
      )}

      <Detalhe item={aberto} periodo={periodo} onFechar={() => setAberto(null)} onFixar={(item, valor) => setFixar({ item, valor })} />

      <ConfirmDialog
        isOpen={!!fixar}
        onClose={() => setFixar(null)}
        onConfirm={confirmarFixar}
        isLoading={fixando}
        title={fixar?.valor === "fora" ? "Tirar da campanha" : fixar?.valor === "dentro" ? "Manter sempre" : "Devolver à regra"}
        confirmText="Confirmar"
        body={textoFixar}
      />
    </Box>
  );
}
